import { useMemo, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@design-system';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  CARRIER_CATALOG,
  carrierSupportsJoint,
  getPromptTypeForCarrier,
  type CarrierName,
} from '@shared/underwriting';
import {
  useCreateWizardEvalTestCase,
  useDeleteWizardEvalTestCase,
  useFetchIntakePayload,
  useUpdateWizardEvalTestCase,
  useWizardEvalRunsByTestCaseId,
} from '@/features/wizard-evals/hooks/use-wizard-evals';
import { RuleMultiSelect } from '@/features/wizard-evals/components/RuleMultiSelect';
import type { WizardEvalTestCase } from '@/features/wizard-evals/api/wizard-evals-api';

type Outcome = 'eligible' | 'ineligible' | 'unsure';

function ruleSuffix(ruleId: string): string {
  const lastDot = ruleId.lastIndexOf('.');
  return lastDot === -1 ? ruleId : ruleId.slice(lastDot + 1);
}

function deriveTestCaseName(input: {
  carrier: CarrierName;
  isJoint: boolean;
  expectedOutcome: Outcome;
  expectedRuleIds: string[];
}): string {
  const parts: string[] = [input.carrier];
  if (input.isJoint) parts.push('joint');
  parts.push(input.expectedOutcome);
  if (input.expectedRuleIds.length > 0) {
    parts.push(input.expectedRuleIds.map(ruleSuffix).join(', '));
  }
  return parts.join(' · ');
}

function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3 border-t border-gray-200 pt-5 first:border-t-0 first:pt-0">
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600">{title}</h4>
        {description && <p className="mt-0.5 text-xs text-gray-600">{description}</p>}
      </div>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
}

interface TestCaseFormProps {
  mode: 'create' | 'edit';
  testCase?: WizardEvalTestCase;
  onSuccess?: (tc: WizardEvalTestCase) => void;
  onCancel?: () => void;
  onDeleted?: (testCaseId: string) => void;
}

export function TestCaseForm({
  mode,
  testCase,
  onSuccess,
  onCancel,
  onDeleted,
}: TestCaseFormProps) {
  const isEdit = mode === 'edit';

  const [clientId, setClientId] = useState(testCase?.sourceClientId ?? '');
  const [snapshotPayload, setSnapshotPayload] = useState<unknown | null>(
    testCase?.intakePayload ?? null,
  );

  const [description, setDescription] = useState(testCase?.description ?? '');
  const [carrier, setCarrier] = useState<CarrierName>(
    (testCase?.carrier as CarrierName) ?? 'nationwide',
  );
  const [isJoint, setIsJoint] = useState(testCase?.isJoint ?? false);
  const [expectedOutcome, setExpectedOutcome] = useState<Outcome>(
    (testCase?.expectedOutcome as Outcome) ?? 'eligible',
  );
  const [expectedRateClass, setExpectedRateClass] = useState(testCase?.expectedRateClass ?? '');
  const [expectedRuleIds, setExpectedRuleIds] = useState<string[]>(testCase?.expectedRuleIds ?? []);
  const [unexpectedRuleIds, setUnexpectedRuleIds] = useState<string[]>(
    testCase?.unexpectedRuleIds ?? [],
  );

  const fetchIntake = useFetchIntakePayload();
  const createTestCase = useCreateWizardEvalTestCase();
  const updateTestCase = useUpdateWizardEvalTestCase(testCase?.testCaseId ?? '');
  const deleteTestCaseMutation = useDeleteWizardEvalTestCase();
  const runsQuery = useWizardEvalRunsByTestCaseId(isEdit ? (testCase?.testCaseId ?? '') : '');
  const priorRunCount = isEdit ? (runsQuery.data?.runs?.length ?? 0) : 0;

  const jointAllowed = carrierSupportsJoint(carrier);
  const effectiveIsJoint = jointAllowed && isJoint;
  const hasSnapshot = snapshotPayload !== null;

  const derivedName = useMemo(
    () =>
      deriveTestCaseName({
        carrier,
        isJoint: effectiveIsJoint,
        expectedOutcome,
        expectedRuleIds,
      }),
    [carrier, effectiveIsJoint, expectedOutcome, expectedRuleIds],
  );

  const handleFetchIntake = async () => {
    const trimmed = clientId.trim();
    if (!trimmed) return;
    try {
      const res = await fetchIntake.mutateAsync(trimmed);
      setSnapshotPayload(res.typeformPayload);
    } catch (err: any) {
      toast.error('Failed to fetch intake', {
        description: err?.message ?? 'Unknown error',
      });
    }
  };

  const handleSubmit = async () => {
    try {
      if (isEdit) {
        const updated = await updateTestCase.mutateAsync({
          name: derivedName,
          description: description.trim(),
          carrier,
          promptType: getPromptTypeForCarrier(carrier, effectiveIsJoint),
          isJoint: effectiveIsJoint,
          expectedOutcome,
          expectedRateClass: expectedRateClass?.trim() || null,
          expectedRuleIds,
          unexpectedRuleIds,
        });
        toast.success('Test case saved');
        onSuccess?.(updated);
      } else {
        if (!snapshotPayload) {
          toast.error('Snapshot required', {
            description: 'Fetch the intake payload before saving.',
          });
          return;
        }
        const created = await createTestCase.mutateAsync({
          name: derivedName,
          description: description.trim(),
          carrier,
          promptType: getPromptTypeForCarrier(carrier, effectiveIsJoint),
          intakePayload: snapshotPayload,
          isJoint: effectiveIsJoint,
          expectedOutcome,
          expectedRateClass: expectedRateClass?.trim() || null,
          expectedRuleIds,
          unexpectedRuleIds,
          sourceClientId: clientId.trim() || null,
        });
        toast.success('Test case created', {
          description: `"${derivedName}" saved to the catalog.`,
        });
        onSuccess?.(created);
      }
    } catch (err: any) {
      toast.error(isEdit ? 'Failed to save' : 'Failed to create', {
        description: err?.message ?? 'Unknown error',
      });
    }
  };

  const handleDelete = async () => {
    if (!testCase) return;
    try {
      await deleteTestCaseMutation.mutateAsync(testCase.testCaseId);
      toast.success('Test case deleted', {
        description: `"${testCase.name}" and its run history were removed.`,
      });
      onDeleted?.(testCase.testCaseId);
    } catch (err: any) {
      toast.error('Failed to delete', {
        description: err?.message ?? 'Unknown error',
      });
    }
  };

  const pending = createTestCase.isPending || updateTestCase.isPending;
  const deleting = deleteTestCaseMutation.isPending;

  return (
    <div className="flex flex-col gap-6">
      {isEdit && priorRunCount > 0 && (
        <div className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">
              {priorRunCount === 1
                ? 'This test case has 1 prior run.'
                : `This test case has ${priorRunCount} prior runs.`}
            </p>
            <p className="mt-0.5">
              Metrics are computed on read against the current expected values, so changes to
              expected outcome, expected rules, or unexpected rules will retroactively re-score
              every past run.
            </p>
          </div>
        </div>
      )}
      {!isEdit && (
        <FormSection
          title="Source client"
          description="Fetch the intake payload. The test case pins this exact snapshot forever."
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="eval-client-id">Client ID</Label>
            <div className="flex items-center gap-2">
              <Input
                id="eval-client-id"
                value={clientId}
                onChange={e => setClientId(e.target.value)}
                placeholder="e.g. nek-yfjm"
                disabled={hasSnapshot}
              />
              <Button
                type="button"
                onClick={handleFetchIntake}
                disabled={!clientId.trim() || fetchIntake.isPending || hasSnapshot}
              >
                {fetchIntake.isPending ? 'Fetching…' : hasSnapshot ? 'Fetched' : 'Fetch intake'}
              </Button>
            </div>
          </div>
        </FormSection>
      )}

      {(hasSnapshot || isEdit) && (
        <>
          <FormSection title="Identity">
            <div className="flex flex-col gap-2">
              <Label htmlFor="eval-name">
                Name <span className="text-gray-600">(auto-generated)</span>
              </Label>
              <Input
                id="eval-name"
                value={derivedName}
                disabled
                readOnly
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="eval-description">
                Description <span className="text-gray-600">(optional)</span>
              </Label>
              <Textarea
                id="eval-description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={2}
                placeholder="Why this case matters / what it tests"
              />
            </div>
          </FormSection>

          <FormSection
            title="Configuration"
            description="Which carrier prompt to run and whether this is a joint policy."
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label>
                  Carrier
                  {isEdit && <span className="ml-1 text-gray-600">(locked after creation)</span>}
                </Label>
                <Select
                  value={carrier}
                  onValueChange={v => setCarrier(v as CarrierName)}
                  disabled={isEdit}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CARRIER_CATALOG.map(c => (
                      <SelectItem
                        key={c.name}
                        value={c.name}
                      >
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <Label>Joint policy</Label>
                <div className="flex h-10 items-center gap-2">
                  <Checkbox
                    id="eval-is-joint"
                    checked={effectiveIsJoint}
                    disabled={!jointAllowed}
                    onCheckedChange={checked => setIsJoint(checked === true)}
                  />
                  <label
                    htmlFor="eval-is-joint"
                    className="text-sm text-gray-700"
                  >
                    {jointAllowed ? 'Treat as joint policy' : 'Not supported for this carrier'}
                  </label>
                </div>
              </div>
            </div>
          </FormSection>

          <FormSection
            title="Expected results"
            description="What the pipeline should produce for this snapshot."
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label>Expected outcome</Label>
                <Select
                  value={expectedOutcome}
                  onValueChange={v => setExpectedOutcome(v as Outcome)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="eligible">eligible</SelectItem>
                    <SelectItem value="ineligible">ineligible</SelectItem>
                    <SelectItem value="unsure">unsure</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="eval-rate-class">
                  Expected rate class <span className="text-gray-600">(optional)</span>
                </Label>
                <Input
                  id="eval-rate-class"
                  value={expectedRateClass ?? ''}
                  onChange={e => setExpectedRateClass(e.target.value)}
                  placeholder="e.g. select"
                />
              </div>
            </div>
          </FormSection>

          <FormSection
            title="Rule expectations"
            description="Which rules the pipeline must (or must not) flag."
          >
            <div className="flex flex-col gap-2">
              <Label>Expected rule IDs</Label>
              <RuleMultiSelect
                carrier={carrier}
                value={expectedRuleIds}
                onChange={setExpectedRuleIds}
                placeholder="Select rules the pipeline should flag…"
                disallowed={new Set(unexpectedRuleIds)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label>
                Unexpected rule IDs <span className="text-gray-600">(must NOT flag)</span>
              </Label>
              <RuleMultiSelect
                carrier={carrier}
                value={unexpectedRuleIds}
                onChange={setUnexpectedRuleIds}
                placeholder="Select rules the pipeline must not flag…"
                disallowed={new Set(expectedRuleIds)}
              />
            </div>
          </FormSection>

          <details className="rounded border border-gray-200 p-2 text-xs">
            <summary className="cursor-pointer text-gray-700">Snapshot payload</summary>
            <pre className="mt-2 max-h-60 overflow-auto text-[11px] leading-snug text-gray-700">
              {JSON.stringify(snapshotPayload, null, 2)}
            </pre>
          </details>

          <div className="flex items-center justify-between gap-2 border-t border-gray-200 pt-4">
            <div>
              {isEdit && testCase && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                      disabled={deleting}
                    >
                      <Trash2 className="mr-1 h-4 w-4" />
                      {deleting ? 'Deleting…' : 'Delete'}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this test case?</AlertDialogTitle>
                      <AlertDialogDescription>
                        <span className="font-medium text-gray-900">{testCase.name}</span> and all
                        of its run history will be permanently removed. This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDelete}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
            <div className="flex gap-2">
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={pending}
              >
                {pending
                  ? isEdit
                    ? 'Saving…'
                    : 'Creating…'
                  : isEdit
                    ? 'Save changes'
                    : 'Create test case'}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
